/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import { isNilOrEmpty } from 'ramda-extension';
import { connect } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import Panel from 'components/atoms/GefMui/Panel/Panel';
import PaperWithToolbar from 'components/atoms/GefMui/PaperWithToolbar';
import SubmitFormDialog from 'components/atoms/GefMui/SubmitFormDialog/SubmitFormDialog';
import { ComponentLoader } from 'components/index';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import { getManagers } from 'features/other/organizationStructureMUI/managers/selectors';
import campaignDetail from 'features/views/campaignManagement/campaignDetail';
import nominations from 'features/views/campaignNominations';
import { CAMPAIGN_SUBSELLER_NOMINATIONS_FORM_NAME } from 'features/views/campaignSubsellerNomination/form';
import { getCampaignSubsellerNominationsFilter } from 'features/views/campaignSubsellerNomination/selectors';

import Filter from './Filter';
import Manager from './Manager';
import Toolbar from './Toolbar';

const CampaignSubsellerNominations = (props) => {
	const {
		fetchNominatedSellers,
		fetchNominationsCount,
		subsellerNominationsFilter,
		fetchCampaignDetail,
		resetSubmitFormDialogErrors,
		campaignDetail,
		campaignId,
		managers,
	} = props;
	const { channelId } = campaignDetail;
	const [countsByLayer, setCountsByLayer] = useState({});

	const updateManagerCounts = useCallback(() => {
		if (!isNaN(channelId) && subsellerNominationsFilter.region && subsellerNominationsFilter.agency) {
			fetchNominationsCount(campaignId, {
				regionId: subsellerNominationsFilter.region,
				agencyIds: [subsellerNominationsFilter.agency],
			}).then((nominationCountsByLayer) => {
				setCountsByLayer(nominationCountsByLayer);
			});
		}
	}, [channelId, campaignId, subsellerNominationsFilter]);

	const handleReload = useCallback(() => {
		fetchCampaignDetail(campaignId);
		updateManagerCounts();
	}, [campaignId, subsellerNominationsFilter.region, subsellerNominationsFilter.agency]);

	useEffect(() => {
		fetchCampaignDetail(campaignId);
	}, [campaignId]);

	useEffect(() => {
		updateManagerCounts();
	}, [campaignId, channelId, subsellerNominationsFilter.region, subsellerNominationsFilter.agency]);

	useEffect(() => {
		if (campaignDetail.isNominatedCampaign) {
			fetchNominatedSellers(campaignId);
		}
	}, [campaignId, campaignDetail.isNominatedCampaign]);

	useEffect(
		() => () => {
			resetSubmitFormDialogErrors({ page: CAMPAIGN_SUBSELLER_NOMINATIONS_FORM_NAME });
		},
		[],
	);

	const noResults = useMemo(() => <Alert severity="info">{getLabelMessage('error:noResults')}</Alert>, []);

	const filteredManagers = managers.filter((manager) => {
		if (subsellerNominationsFilter.manager) {
			if (manager.login !== subsellerNominationsFilter.manager) {
				return false;
			}
		}
		return countsByLayer[manager.login] !== undefined;
	});

	return (
		<ComponentLoader
			fetchStorePath={fetch.constants.urls.campaignDetailWithoutConsideringRole}
			onReload={handleReload}
		>
			<ComponentLoader fetchStorePath={`${fetch.constants.urls.orgStructureManagers}.list`}>
				<Panel
					screenId={PAGES.campaignSubsellerNominations.id}
					screenName={PAGES.campaignSubsellerNominations.name}
					floatingDialogs={<SubmitFormDialog pageName={CAMPAIGN_SUBSELLER_NOMINATIONS_FORM_NAME} />}
				>
					<PaperWithToolbar
						toggledElement={<Filter />}
						toolbar={<Toolbar campaignDetail={campaignDetail} />}
						collapsibleProps={{ id: subheaders.campaignSubsellerNominations }}
					>
						{!isNilOrEmpty(filteredManagers)
							? filteredManagers.map((manager) => (
									<Manager
										key={`${manager.login} ${filteredManagers.length === 1}`}
										manager={manager}
										campaignId={campaignId}
										channelId={channelId}
										regionId={subsellerNominationsFilter.region}
										agencyId={subsellerNominationsFilter.agency}
										updateManagerCounts={updateManagerCounts}
										count={countsByLayer[manager.login]}
										isTheOnlyManager={filteredManagers.length === 1}
									/>
							  ))
							: noResults}
					</PaperWithToolbar>
				</Panel>
			</ComponentLoader>
		</ComponentLoader>
	);
};

CampaignSubsellerNominations.propTypes = {
	campaignDetail: PropTypes.object,
	campaignId: PropTypes.number,
	fetchCampaignDetail: PropTypes.func,
	fetchNominatedSellers: PropTypes.func,
	fetchNominationsCount: PropTypes.func,
	managers: PropTypes.array,
	resetSubmitFormDialogErrors: PropTypes.func,
	subsellerNominationsFilter: PropTypes.object,
};

export default connect(
	(state) => {
		const subsellerNominationsFilter = getCampaignSubsellerNominationsFilter(state);
		return {
			campaignDetail: campaignDetail.selectors.getCampaignDetail(state),
			subsellerNominationsFilter,
			managers: getManagers(state, subsellerNominationsFilter),
		};
	},
	{
		fetchNominatedSellers: nominations.actions.fetchNominatedSellers,
		fetchCampaignDetail: nominations.actions.fetchCampaignDetail,
		fetchNominationsCount: nominations.actions.fetchNominationsCount,
		resetSubmitFormDialogErrors: resetErrorsByPage,
	},
)(CampaignSubsellerNominations);
